// src/lib/resources/types/templates/base.ts

/**
 * @fileoverview Core template types and interfaces
 * This is the single source of truth for base template types
 */

import type { BaseResource } from '~/lib/resources/types/shared/base'
import { type DifficultyLevel, ResourceType } from '~/lib/resources/types/shared/enums'
import type { GenericTemplateConfig } from '~/components/forms/base/types'
import type { ComponentType, SVGProps } from 'react'

/**
 * Template types focused on marketing content
 * 
 * @typedef {Object} TemplateTypes
 * @property {string} BUYER_PERSONA - Template for creating customer personas
 * @property {string} CAMPAIGN_BRIEF - Template for campaign briefs
 * @property {string} CONTENT_CALENDAR - Template for content planning calendars
 * @property {string} CREATIVE_BRIEF - Template for creative briefs
 * @property {string} CAMPAIGN_BUDGET - Template for campaign budget planning
 * @property {string} CAMPAIGN_PLANNER - Template for comprehensive campaign planning
 * @property {string} CONTENT_AUDIT - Template for content auditing
 * @property {string} MARKETING_PLAN - Template for marketing plans
 * @property {string} SOCIAL_MEDIA_PLAN - Template for social media planning
 * @property {string} CONTENT_STYLE_GUIDE - Template for content style guides
 * @property {string} MEDIA_PLAN - Template for media planning
 * @property {string} PERFORMANCE_REPORT - Template for performance reporting
 * @property {string} BUDGET_TRACKER - Template for budget tracking
 * @property {string} COMPETITOR_ANALYSIS - Template for competitor analysis
 * @property {string} CHANNEL_STRATEGY - Template for channel strategy planning
 * @property {string} CREATIVE_TESTING - Template for creative testing frameworks
 */
export const TemplateTypes = {
  BUYER_PERSONA: 'buyer_persona',
  CAMPAIGN_BRIEF: 'campaign_brief', 
  CONTENT_CALENDAR: 'content_calendar',
  CREATIVE_BRIEF: 'creative_brief',
  CAMPAIGN_BUDGET: 'campaign_budget',
  CAMPAIGN_PLANNER: 'campaign_planner',
  CONTENT_AUDIT: 'content_audit',
  MARKETING_PLAN: 'marketing_plan',
  SOCIAL_MEDIA_PLAN: 'social_media_plan',
  CONTENT_STYLE_GUIDE: 'content_style_guide',
  MEDIA_PLAN: 'media_plan',
  PERFORMANCE_REPORT: 'performance_report',
  BUDGET_TRACKER: 'budget_tracker',
  COMPETITOR_ANALYSIS: 'competitor_analysis',
  CHANNEL_STRATEGY: 'channel_strategy',
  CREATIVE_TESTING: 'creative_testing'
} as const

/**
 * Type representing valid template types
 * 
 * @typedef {string} TemplateType
 */
export type TemplateType = typeof TemplateTypes[keyof typeof TemplateTypes]

/**
 * Template delivery formats with associated metadata
 * 
 * @typedef {Object} TemplateDeliveryFormats
 * @property {Object} PDF - PDF format configuration
 * @property {Object} GOOGLE_DOCS - Google Docs format configuration
 * @property {Object} GOOGLE_SHEETS - Google Sheets format configuration
 * @property {Object} GOOGLE_SLIDES - Google Slides format configuration
 * @property {Object} EXCEL - Excel format configuration
 * @property {Object} WORD - Word format configuration
 * @property {Object} POWERPOINT - PowerPoint format configuration
 * @property {Object} CANVA - Canva format configuration
 * @property {Object} FIGMA - Figma format configuration
 * @property {Object} CSV - CSV spreadsheet configuration
 */
export const TemplateDeliveryFormats = {
  PDF: {
    id: 'pdf',
    name: 'PDF Template',
    description: 'Fillable PDF document'
  },
  GOOGLE_DOCS: {
    id: 'google_docs',
    name: 'Google Docs',
    description: 'Editable Google Document'
  },
  GOOGLE_SHEETS: {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Spreadsheet with formulas and calculations'
  },
  GOOGLE_SLIDES: {
    id: 'google_slides',
    name: 'Google Slides',
    description: 'Google Slides presentation'
  },
  EXCEL: {
    id: 'excel',
    name: 'Excel',
    description: 'Microsoft Excel spreadsheet'
  },
  WORD: {
    id: 'word',
    name: 'Word',
    description: 'Microsoft Word document'
  },
  POWERPOINT: {
    id: 'powerpoint',
    name: 'PowerPoint',
    description: 'Microsoft PowerPoint presentation'
  },
  CANVA: {
    id: 'canva',
    name: 'Canva',
    description: 'Canva design template'
  },
  FIGMA: {
    id: 'figma',
    name: 'Figma',
    description: 'Figma design file'
  },
  CSV: {
    id: 'csv',
    name: 'CSV Spreadsheet',
    description: 'Importable CSV — opens in Google Sheets, Excel, or Numbers'
  }
} as const

/**
 * Type representing the keys of template delivery formats
 * 
 * @typedef {string} TemplateDeliveryFormatType
 */
export type TemplateDeliveryFormatType = keyof typeof TemplateDeliveryFormats

/**
 * Type representing the ID values of template delivery formats
 * 
 * @typedef {string} TemplateDeliveryFormatId
 */
export type TemplateDeliveryFormatId = typeof TemplateDeliveryFormats[TemplateDeliveryFormatType]['id']

/**
 * Template categories focused on marketing use cases
 * 
 * @typedef {Object} TemplateCategoryTypes
 * @property {string} CAMPAIGN_PLANNING - Campaign planning templates
 * @property {string} CONTENT_STRATEGY - Content strategy templates
 * @property {string} AUDIENCE_RESEARCH - Audience research templates
 * @property {string} BUDGET_PLANNING - Budget planning templates
 * @property {string} CREATIVE_DEVELOPMENT - Creative development templates
 * @property {string} CREATIVE_STRATEGY - Creative strategy templates
 * @property {string} PERFORMANCE_TRACKING - Performance tracking templates
 */
export const TemplateCategoryTypes = {
  CAMPAIGN_PLANNING: 'campaign_planning',
  CONTENT_STRATEGY: 'content_strategy',
  AUDIENCE_RESEARCH: 'audience_research',
  BUDGET_PLANNING: 'budget_planning',
  CREATIVE_DEVELOPMENT: 'creative_development',
  CREATIVE_STRATEGY: 'creative_strategy',
  PERFORMANCE_TRACKING: 'performance_tracking'
} as const

/**
 * Type representing valid template category types
 * 
 * @typedef {string} TemplateCategoryType
 */
export type TemplateCategoryType = typeof TemplateCategoryTypes[keyof typeof TemplateCategoryTypes]

/**
 * Display names for template categories
 * Maps category identifiers to human-readable names
 * 
 * @type {Record<TemplateCategoryType, string>}
 */
export const TemplateCategoryDisplayNames: Record<TemplateCategoryType, string> = {
  campaign_planning: 'Campaign Planning',
  content_strategy: 'Content Strategy',
  audience_research: 'Audience Research',
  budget_planning: 'Budget Planning',
  creative_development: 'Creative Development',
  creative_strategy: 'Creative Strategy',
  performance_tracking: 'Performance Tracking'
} as const

/**
 * Template feature with icon support
 * 
 * @interface TemplateFeature
 * @property {string} name - Feature name
 * @property {string} description - Feature description
 * @property {ComponentType<SVGProps<SVGSVGElement>>} icon - SVG icon component
 * @property {string} [category] - Optional category for grouping features
 */
export interface TemplateFeature {
  name: string
  description: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  category?: string
}

/**
 * Format-specific configuration for a template
 * 
 * @interface FormatConfig
 * @property {TemplateDeliveryFormatId} formatId - The format identifier
 * @property {string} contentUrl - URL to the actual content
 * @property {string} [previewUrl] - Preview URL specific to this format
 * @property {boolean} [isGated] - Whether this format requires gating
 * @property {Object} [embedConfig] - Embed configuration for this format if it supports embedding
 * @property {string} embedConfig.url - URL for embedding
 * @property {string} [embedConfig.oEmbedUrl] - oEmbed URL if supported
 * @property {boolean} embedConfig.oEmbedFlag - Whether oEmbed is supported
 * @property {'canva'|'figma'|'google'|'microsoft'} embedConfig.type - Type of embed
 * @property {Object} [embedConfig.settings] - Additional embed settings
 * @property {Object} [metadata] - Format-specific metadata
 */
export interface FormatConfig {
  /** The format identifier */
  formatId: TemplateDeliveryFormatId
  /** URL to the actual content */
  contentUrl: string
  /** Preview URL specific to this format */
  previewUrl?: string
  /** Whether this format requires gating */
  isGated?: boolean
  /** Embed configuration for this format if it supports embedding */
  embedConfig?: {
    url: string
    oEmbedUrl?: string
    oEmbedFlag: boolean
    type: 'canva' | 'figma' | 'google' | 'microsoft'
    settings?: {
      mode?: 'preview' | 'edit'
      hideControls?: boolean
      startPage?: number
    }
  }
  /** Format-specific metadata */
  metadata?: {
    version?: string
    lastUpdated?: string
    fileSize?: string
    pageCount?: number
  }
}

/**
 * Template source configuration
 * 
 * @interface TemplateSource
 * @property {FormatConfig[]} formats - Available format configurations
 * @property {string} [defaultPreviewUrl] - Default preview image URL
 * @property {string} [defaultThumbnailUrl] - Default thumbnail URL
 */
export interface TemplateSource {
  /** Available format configurations */
  formats: FormatConfig[]
  /** Default preview image URL */
  defaultPreviewUrl?: string
  /** Default thumbnail URL */
  defaultThumbnailUrl?: string
}

/**
 * Template usage instructions
 * 
 * @interface TemplateUsage
 * @property {string[]} [prerequisites] - Prerequisites for using the template
 * @property {string} [instructions] - Usage instructions
 * @property {Object[]} [tips] - Usage tips
 * @property {Object[]} [examples] - Usage examples
 */
export interface TemplateUsage {
  prerequisites?: string[]
  instructions?: string
  tips?: {
    title: string
    content: string
    category?: string
  }[]
  examples?: {
    title: string
    description: string
    imageUrl?: string
  }[]
}

/**
 * Template FAQ
 * 
 * @interface TemplateFAQ
 * @property {string} question - FAQ question
 * @property {string} answer - FAQ answer
 * @property {string} [category] - Optional category for grouping FAQs
 */
export interface TemplateFAQ {
  question: string
  answer: string
  category?: string
}

/**
 * Template interaction types - how users can work with the template
 * 
 * @typedef {Object} TemplateInteractionTypes
 * @property {string} VIEW_ONLY - User can only view the template
 * @property {string} DOWNLOADABLE - User can download the template
 * @property {string} EDITABLE - User can edit the template
 * @property {string} INTERACTIVE - User can interact with the template
 * @property {string} GENERATIVE - Template has AI generation capabilities
 */
export const TemplateInteractionTypes = {
  VIEW_ONLY: 'view_only',
  DOWNLOADABLE: 'downloadable',
  EDITABLE: 'editable',
  INTERACTIVE: 'interactive',
  GENERATIVE: 'generative' // For future AI-powered templates
} as const

/**
 * Type representing valid template interaction types
 * 
 * @typedef {string} TemplateInteractionType
 */
export type TemplateInteractionType = typeof TemplateInteractionTypes[keyof typeof TemplateInteractionTypes]

/**
 * Template access control
 * 
 * @interface TemplateAccess
 * @property {boolean} hasGatedFormats - Whether the template has any gated formats
 * @property {Object} [defaultGating] - Default gating configuration for formats that don't specify their own
 */
export interface TemplateAccess {
  /** Whether the template has any gated formats */
  hasGatedFormats: boolean
  /** Default gating configuration for formats that don't specify their own */
  defaultGating?: {
    type: 'email' | 'subscription' | 'enterprise' | 'custom'
    requirements?: {
      emailCapture?: {
        template: GenericTemplateConfig
        successMessage?: string
      }
      subscription?: {
        minimumTier: string
        upgradeMessage?: string
      }
      custom?: Record<string, unknown>
    }
  }
}

/**
 * Base template configuration
 * 
 * @interface BaseTemplateConfig
 * @property {TemplateSource} source - Template source configuration
 * @property {TemplateAccess} [access] - Template access control
 * @property {TemplateUsage} usage - Template usage instructions
 * @property {TemplateFeature[]} [features] - Template features
 * @property {TemplateFAQ[]} [faqs] - Template FAQs
 * @property {Object[]} [platforms] - Supported platforms
 */
export interface BaseTemplateConfig {
  source: TemplateSource
  access?: TemplateAccess
  usage: TemplateUsage
  features?: TemplateFeature[]
  faqs?: TemplateFAQ[]
  platforms?: {
    name: string
    notes?: string
    requirements?: string[]
  }[]
}

/**
 * Core template interface that all template types extend
 * 
 * @interface Template
 * @extends {BaseResource}
 * @property {typeof ResourceType.TEMPLATE} resourceType - Resource type identifier
 * @property {TemplateType} templateType - Template type
 * @property {TemplateCategoryType} category - Template category
 * @property {Object[]} sections - Template content sections
 * @property {BaseTemplateConfig} config - Template configuration
 * @property {string} [estimatedTime] - Estimated time to complete
 * @property {DifficultyLevel} [difficulty] - Template difficulty level
 */
export interface Template extends BaseResource {
  resourceType: typeof ResourceType.TEMPLATE
  templateType: TemplateType
  category: TemplateCategoryType
  sections: {
    id: string
    title: string
    content: string
  }[]
  config: BaseTemplateConfig
  estimatedTime?: string
  difficulty?: DifficultyLevel
}

/**
 * Type guard to check if a value is a template
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a valid Template
 */
export function isTemplate(value: unknown): value is Template {
  if (typeof value !== 'object' || value === null) return false
  
  const template = value as Partial<Template>
  return (
    template.resourceType === ResourceType.TEMPLATE &&
    typeof template.config === 'object' &&
    template.config !== null &&
    'source' in template.config &&
    typeof template.config.source === 'object' &&
    template.config.source !== null &&
    Array.isArray((template.config.source).formats) &&
    Array.isArray(template.sections)
  )
}